
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { TranslateResult } from "vue-i18n";
import CanvasCourseMapping from "@/types/Canvas/CanvasCourseMapping";
import CanvasCourseMappingApiService from "@/services/CanvasCourseMappingApiService";
import EnumerationList from "@/types/Enumeration/EnumerationList";
import TrainingType from "@/types/Training/TrainingType";
import VMSTextField from "@/components/fields/VMSTextField.vue";
import VMSSelect from "@/components/fields/VMSSelect.vue";
import EnumerationSelect from "@/components/EnumerationSelect.vue";
import Guid from "@/types/Guid";

@Component({
  components: {
    VMSTextField,
    VMSSelect,
    EnumerationSelect
  }
})
export default class CanvasCourseMappingForm extends Vue {
  @Prop(Boolean) value: boolean | undefined;
  @Prop(String) dialogTitle: string | undefined;
  @Prop(Object) mapping: CanvasCourseMapping | undefined;
  @Prop(Array) trainingTypes: TrainingType[] | undefined;
  @Prop(Object) trainingDeliveredList: EnumerationList | undefined;
  @Prop(Object) skillsList: EnumerationList | undefined;
  @Prop(Object) deliveryFormatsList: EnumerationList | undefined;

  api = new CanvasCourseMappingApiService();
  localMapping: CanvasCourseMapping = new CanvasCourseMapping();
  selectedSkillIds: string[] = [];
  selectedDeliveryFormatIds: string[] = [];

  get dialogVisible(): boolean {
    return !!this.value;
  }
  set dialogVisible(v: boolean) {
    if (!v) this.closeDialog();
  }

  // The manual log form (TrainingCreate.vue) stores a free-text training as the
  // "Other" catalog item plus OtherTraining, so the mapping must do the same or the
  // auto-logged Training row cannot be displayed. Resolve "Other" by its i18n key
  // rather than by id so the form does not depend on a hard-coded guid.
  get otherTrainingId(): string | undefined {
    const items =
      (this.trainingDeliveredList &&
        this.trainingDeliveredList.EnumerationItems) ||
      [];
    const other = items.find(
      eli => eli.Enumeration.LocalizationKey === "vmslc.Other"
    );
    return other ? other.EnumerationId : undefined;
  }

  get isOtherSelected(): boolean {
    return (
      !!this.otherTrainingId &&
      this.localMapping.TrainingDeliveredId === this.otherTrainingId
    );
  }

  get skillItems(): { value: string; text: string | TranslateResult }[] {
    return this.enumItemsOf(this.skillsList);
  }

  get deliveryFormatItems(): {
    value: string;
    text: string | TranslateResult;
  }[] {
    return this.enumItemsOf(this.deliveryFormatsList);
  }

  // Labels resolve through i18n via LocalizationKey — matches EnumerationSelect.vue.
  // Falls back to Description (then Value) when no i18n key exists.
  enumItemsOf(
    list: EnumerationList | undefined
  ): { value: string; text: string | TranslateResult }[] {
    const items = (list && list.EnumerationItems) || [];
    return [...items]
      .sort((a, b) => a.SortOrder - b.SortOrder)
      .map(eli => {
        const e = eli.Enumeration;
        const text = e.LocalizationKey
          ? this.$t(e.LocalizationKey)
          : e.Description || e.Value || "";
        return { value: eli.EnumerationId, text };
      });
  }

  @Watch("mapping", { immediate: true })
  onMappingChanged() {
    this.localMapping = this.mapping
      ? { ...this.mapping }
      : new CanvasCourseMapping();

    try {
      this.selectedSkillIds = JSON.parse(
        this.localMapping.SkillsEnumerationIdsJson || "[]"
      );
    } catch {
      this.selectedSkillIds = [];
    }

    try {
      this.selectedDeliveryFormatIds = JSON.parse(
        this.localMapping.DeliveryFormatEnumerationIdsJson || "[]"
      );
    } catch {
      this.selectedDeliveryFormatIds = [];
    }
  }

  closeDialog() {
    // Parent uses `v-model="formDialog"`, which is sugar for
    // `:value="formDialog" @input="formDialog = $event"`. Emitting `input` is what
    // syncs the open-state back. (Click-outside also routes through this setter via
    // dialogVisible, so emitting "update:value" here would leave the parent's
    // formDialog stuck at true and prevent reopening.)
    this.$emit("input", false);
  }

  async save() {
    if (!(this.$refs.form as any).validate()) return;

    this.localMapping.SkillsEnumerationIdsJson = JSON.stringify(
      this.selectedSkillIds || []
    );

    this.localMapping.DeliveryFormatEnumerationIdsJson = JSON.stringify(
      this.selectedDeliveryFormatIds || []
    );

    // The free-text name only has meaning alongside the "Other" catalog item.
    if (!this.isOtherSelected) {
      this.localMapping.OtherTrainingName = null;
    }

    const isNew = !this.localMapping.Id || this.localMapping.Id === Guid.Empty;

    // Wrap in try/catch because the global axios interceptor (App.vue) swallows
    // error responses and resolves the promise with `undefined`, which would
    // crash on `resp.Success` and lose the user's form input.
    let resp:
      | {
          Success: boolean;
          Message?: string;
          Errors?: string[];
          Value?: unknown;
        }
      | undefined;
    try {
      resp = isNew
        ? await this.api.CreateMapping(this.localMapping)
        : await this.api.UpdateMapping(this.localMapping);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(
        "[CanvasCourseMapping] save threw:",
        err,
        "payload:",
        this.localMapping
      );
    }

    if (!resp) {
      // Global interceptor already showed the AxiosErrorText snackbar; log the
      // payload so we can diagnose unexpected 400s from the backend.
      // eslint-disable-next-line no-console
      console.warn(
        "[CanvasCourseMapping] save returned undefined (likely 4xx/5xx swallowed by interceptor). Payload was:",
        JSON.parse(JSON.stringify(this.localMapping))
      );
      return;
    }

    if (resp.Success) {
      this.$emit("saved");
      this.closeDialog();
      return;
    }

    // Backend rejected the payload with a structured error. Surface it.
    // eslint-disable-next-line no-console
    console.warn("[CanvasCourseMapping] save rejected by backend:", resp);
    const msg =
      resp.Message || (resp.Errors && resp.Errors.join("; ")) || "Save failed.";
    const snackbar = (this as any).$ShowSnackbar;
    if (typeof snackbar === "function") snackbar(msg);
  }
}
